import { sprinklerTypes } from '@/bundles/Properties/helpers';
import { IBuilding } from '@/utils/interfaces';
import { ITextValue } from '@/types/common';
import { ConstructionStatusEnum, constructionStatusOptions } from '@/bundles/Property/types';

import { OwnerShipTypeEnum } from '../enums';

export interface IBuildingDetailsItems {
  building_use: string[];
  specBts: string[];
  building_class: string[];
  leed_type: string[];
  sprinkler_type: string[];
  lighting_type: string[];
  floor_type: string[];
  roof_type: string[];
  construction_status: ConstructionStatusEnum[];
  exterior_type: string[];
  construction_type: string[];
  tenant_type: string[];
  mezzanine_type: ITextValue<string>[];
  mezzanine_property: ITextValue<string>[];
  ownership_type: ITextValue<OwnerShipTypeEnum>[];
}

export const getBuildingDetailsItems = (): IBuildingDetailsItems => ({
  building_use: [
    'Automotive',
    'Bank',
    'CL & D',
    'Climate Controlled Warehouse',
    'Cold Storage',
    'Data Center',
    'Financial',
    'Flex',
    'Food Processing',
    'Freight Forwarding',
    'Industrial',
    'Industrial Distribution',
    'Industrial Food Processing',
    'Industrial Manufacturing',
    'Investment',
    'Light Industrial',
    'Light Manufacturing',
    'Medical office',
    'Office',
    'Professional office',
    'R & D',
    'Refrigerated Warehouse',
    'Retail',
    'Self-Storage',
    'Showroom',
    'Storage',
    'Warehouse',
  ],
  specBts: ['Spec', 'BTS'],
  building_class: ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-'],
  leed_type: [
    'Platinum',
    'Gold',
    'Silver',
    'Certified',
    'Certified (Level Unknown)',
    'Not Certified',
  ],
  sprinkler_type: sprinklerTypes,
  lighting_type: [
    'Bank',
    'Energy Efficient',
    'Fluorescent',
    'Halogen',
    'Incandescent',
    'LED',
    'Metal Halide',
    'Mixed',
    'Other',
    'Recessed',
    'Sodium',
    'Sodium Vapor',
    'T-5',
    'T-8',
    'Track &amp; Florescent',
    'Track lighting',
    'Various',
  ],
  floor_type: ['Concrete', 'Concrete Post Tension', 'Concrete Superflat'],
  roof_type: [
    '4-ply Built-Up',
    '45 mil EPDM',
    '45 Mil TPO',
    '60 Mil TPO',
    'Asphalt/Built up',
    'Built-up',
    'Composite',
    'Elastomeric',
    'Flat',
    'Flat, Rubber',
    'Gabled',
    'Gabled, Shingle',
    'Gabled, Tile & Rubber',
    'Mansard',
    'Metal',
    'Mixed',
    'Other',
    'Pitched',
    'Pitched/Flat',
    'Shallow Pitch',
    'Shingle',
    'Shingles/Metal',
    'Slate',
    'Standing Seam',
    'Tar/Gravel',
    'TPO R-9',
    'TPO R-19',
    'TPO R-30',
  ],
  construction_status: constructionStatusOptions,
  exterior_type: ['Brick', 'Concrete', 'Metal', 'Stucco', 'Wood'],
  construction_type: [
    'Block',
    'Block/Dryvit',
    'Block/Siding',
    'Brick',
    'Brick, Block w/Metal Canopy',
    'Brick/Block',
    'Brick/Block/Frame',
    'Brick/Brownstone',
    'Brick/Frame',
    'Brick/Masonry',
    'Brick/Steel',
    'Brick/Stone',
    'Brick/Stone/Concrete',
    'Brick/Stone/Glass',
    'Brick/Wood',
    'Cedar Siding',
    'Concrete',
    'Concrete Tilt-Up',
    'Concrete/Block',
    'Dryvit',
    'Dryvit/Brick',
    'Dryvit/Steel',
    'Frame',
    'Frame/Block',
    'Frame/Brick',
    'Frame/Dryvit',
    'Frame/Siding',
    'Frame/Stone',
    'Frame/Vinyl/Brick',
    'Glass/Brick',
    'Glass/Masonry',
    'Glass/Steel',
    'Insulated Metal',
    'Log',
    'Masonry',
    'Masonry/Block',
    'Masonry/Brick/Veener',
    'Masonry/Dryvit',
    'Masonry/Frame',
    'Masonry/Siding',
    'Masonry/Steel',
    'Masonry/Stucco',
    'Masonry/Wood',
    'Metal',
    'Metal/Block',
    'Metal/Brick & Stone',
    'Metal/Masonry',
    'Metal/Split Brick',
    'Other',
    'Pre-Cast',
    'Pre-Engineered Metal',
    'Reinforced Concrete',
    'Split Block',
    'Steel',
    'Steel/Block',
    'Steel/Frame',
    'Stone',
    'Stone/Block',
    'Stone/Brick',
    'Stucco',
    'Williamsburg Brick',
    'Wood',
    'Wood Frame',
  ],
  tenant_type: ['Single Tenant', 'Multi Tenant', 'Condo'],
  mezzanine_type: [
    { value: 'office', text: 'Office' },
    { value: 'warehouse', text: 'Warehouse' },
  ],
  mezzanine_property: [
    { value: 'included in', text: 'Included in' },
    { value: 'in addition to', text: 'In addition to' },
  ],
  ownership_type: [
    { value: OwnerShipTypeEnum.institutional, text: OwnerShipTypeEnum.institutional },
    { value: OwnerShipTypeEnum.private, text: OwnerShipTypeEnum.private },
  ]
});

export const getBuildingDetails = (): IBuilding => ({
  building_count: null,
  ceiling_height: {
    min: null,
    max: null,
  },
  class: null,
  column: {
    height: null,
    width: null,
    depth: null,
  },
  comments: '',
  construction_status: ConstructionStatusEnum.existing,
  construction_type: null,
  date_built: null,
  dimension: {
    width: {
      min: null,
      max: null
    },
    depth: {
      min: null,
      max: null
    },
  },
  exterior_type: null,
  floor_thickness: null,
  heating: null,
  lighting_type: null,
  mezzanine: {
    property: null,
    square_feet: null,
    type: null,
  },
  restroom: null,
  roof_type: null,
  security: null,
  signage: null,
  slab_psi: null,
  sprinkler: null,
  tenant_type: null,
  ventilation: null,
  year_built: null,
  year_refurbished: null,
  total: null,
  rentable: null,
  floor_type: null,
  roof_age: null,
  floor_count: null,
  floor_plate: null,
  private: false,
  spec_bts: null,
  units: null,
  unit_mix: [],
  style: null,
  market_segment: null,
  rent_type: {
    affordable: false,
    market: false,
  },
  has_elevator: false,
  commercial_space: null,
  target_completion_year: null,
  break_ground_date: null,
  date_built_estimated: null,
  basement_floors: null,
  basement_size: null,
  target_completion_quarter: null,
  ownership_type: null,
});

export const getSizeBuildingDetailsForm = () => ({
  building: {
    units: null,
    total: null,
    rentable: null,
  },
  office: {
    total: null,
  }
});
